import {
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
  clearCsvOvertimeReportData,
  clearPtoReportData,
  getCsvOvertimeReport,
  getEmployeeList,
  getOvertimeReport,
} from '../../../store/ReportsDetailsSlice/actions.js';
import {
  selectCsvOvertimeReportData,
  selectEmployeeData,
  selectOvertimeReportData,
} from '../../../store/ReportsDetailsSlice/selectors.js';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter.jsx';
import { saveCsvToFile } from '../../../utils/saveCsvToFile.js';
import { OvertimeTable } from '../../../components/OvertimeTable/OvertimeTable.jsx';
import { usePaginationControls } from '../hooks/usePaginationControl.jsx';
import { GROUPBY } from '../../../constants/Reports';
import SelectRebuild from '../../../components/Inputs/SelectRebuild.jsx';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton.jsx';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const PAGINATION = 60;

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : ['ALL'];

const ReportPayrollSummaryScreen = () => {
  const dispatch = useDispatch();
  const [exportType, setExportType] = useState('');
  const [params, setParams] = useSearchParams();
  const groupByParam = params.get('groupby') || GROUPBY[0].value;
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  // Initialize form with useForm
  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: [],
    },
  });

  const getReportHeaderFields = useMemo(
    () =>
      groupByParam === 'Emp'
        ? ['hours', 'pay_code', 'amount']
        : ['name', 'hours', 'pay_code', 'amount'],
    [groupByParam]
  );

  const { overtimeReportData, loadingOvertimeReport } = useSelector(
    selectOvertimeReportData
  );
  const { csvOvertimeReport, loadingCsvOvertimeReport } = useSelector(
    selectCsvOvertimeReportData
  );
  const tableItems = useMemo(() => {
    return Object.entries(overtimeReportData?.data || []);
  }, [overtimeReportData]);

  const pagesCount = Math.ceil(Object.keys(tableItems).length / PAGINATION);
  const { fromDate, toDate, render } = useDateRangeFilter();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);

  // Get form values
  const { emp_code: employees } = watch();
  const employeeParam = useMemo(() => createParams(employees), [employees]);

  const { paginationComponent, pageParam } = usePaginationControls({
    loading: loadingOvertimeReport,
    pagesCount,
  });

  const headerItems = Object.entries(overtimeReportData?.header || []).filter(
    (it) => getReportHeaderFields.some((hf) => hf === it[0])
  );

  const tableData = useMemo(() => {
    return tableItems.slice(
      (+pageParam - 1) * PAGINATION,
      +pageParam * PAGINATION
    );
  }, [tableItems, pageParam]);

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupByParam,
      emp_code:
        employeeParam.length === 1 && employeeParam[0] === 'ALL'
          ? undefined
          : employeeParam,
    };
  }, [fromDate, toDate, groupByParam, employeeParam]);

  const handleFindClick = () => dispatch(getOvertimeReport(param));

  const handleChangeGroupBy = (e) => {
    const {
      target: { value },
    } = e;
    setParams((prev) => {
      if (!value) {
        prev.delete('groupby');
      } else {
        if (value === 'Group') {
          prev.delete('employee');
        }
        prev.set('groupby', value);
      }
      return prev;
    });
    dispatch(clearPtoReportData());
  };
  const renderGroupByValue = (selected) => {
    return GROUPBY.find((it) => it.value === selected)?.title || '';
  };
  const handleDownloadReport = (type) => {
    setExportType(type);
    dispatch(getCsvOvertimeReport({ ...param, [type]: 1 }));
  };

  useEffect(
    () => {
      dispatch(getEmployeeList());
      dispatch(getOvertimeReport(param));
      return () => dispatch(clearPtoReportData());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(
    () => {
      setParams((prev) => {
        prev.delete('page');
        return prev;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overtimeReportData]
  );
  useEffect(() => {
    if (csvOvertimeReport?.data) {
      saveCsvToFile(csvOvertimeReport.data, csvOvertimeReport.filename);
      dispatch(clearCsvOvertimeReportData());
      setExportType('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvOvertimeReport]);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <Select
          sx={{
            height: '36px',
          }}
          id="Groupby-selector"
          value={groupByParam}
          onChange={handleChangeGroupBy}
          renderValue={renderGroupByValue}
          placeholder="Group by"
          labelId="groupby-select-label"
          fullWidth
        >
          {GROUPBY.map((it) => (
            <MenuItem key={it.value} value={it.value}>
              <ListItemText primary={it.title} />
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={() => handleDownloadReport('payroll_csv')}
            disabled={loadingCsvOvertimeReport && exportType === 'payroll_csv'}
            text={'Export CSV'}
          />
          <ExportCsvButton
            onClick={() => handleDownloadReport('payroll_all_csv')}
            disabled={
              loadingCsvOvertimeReport && exportType === 'payroll_all_csv'
            }
            text={'Export All CSV'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Payroll Summary Report Details" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Box spacing={2} className="my-2">
          <Grid container sx={{ width: '100%', mb: '24px' }}>
            <FilterPanel
              filters={filterConfig.slice(0, 4)}
              columns={isTabletVerticalWidth ? 1 : 4}
            />
          </Grid>

          <Grid container sx={{ width: '100%' }}>
            <Grid
              item
              sx={{
                display: 'flex',
                gap: 2,
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Grid
                item
                sx={{
                  display: 'flex',
                  flexDirection: isTabletWidth ? 'column' : 'row',
                  gap: 2,
                  width: '100%',
                  mt: 'auto',
                }}
              >
                {render}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {loadingOvertimeReport && (
              <Box
                sx={{ minHeight: '100px' }}
                className="w-100 h-100 d-flex justify-content-center align-items-center"
              >
                <CircularProgress />
              </Box>
            )}

            {!loadingOvertimeReport && (
              <OvertimeTable tableData={tableData} headerItems={headerItems} />
            )}
            {!loadingOvertimeReport && !tableData.length && (
              <Box>
                <Grid
                  container
                  justifyContent={'center'}
                  spacing={3}
                  className="my-2"
                >
                  <Box py={4}> No data available</Box>
                </Grid>
              </Box>
            )}
            {paginationComponent}
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
};

export default ReportPayrollSummaryScreen;
